import { useFlag as useUnleashFlag } from "@unleash/nextjs/client";
import { useRouter } from "next/router";

import { useCookieState } from "./helper";

/**
 * This hook allows you to check if a flag is enabled in Unleash and also
 * allows you to override the flag value with a local storage value or a querystring value
 *
 * @param flagName The name of a flag in Unleash
 * @returns A boolean indicating if the flag is enabled
 */
export const useFlag = (flagName: string): boolean => {
  const [cookieFlagOverrideEnabled, setCookieFlagOverride] =
    useCookieState<string>(`flag__${flagName}`, null, {
      maxAge: 60 * 60 * 24 * 30, // 30 days
    });
  const unleashFlagEnabled = useUnleashFlag(flagName);
  const { query } = useRouter();
  const qsFlagOverrideEnabled = query[flagName] === "true";

  if (qsFlagOverrideEnabled && !cookieFlagOverrideEnabled) {
    setCookieFlagOverride("true");
  } else if (query[flagName] === "false" && cookieFlagOverrideEnabled) {
    setCookieFlagOverride(undefined);
  }

  const convertedFlagOverride = convertStringToBoolean(
    cookieFlagOverrideEnabled
  );

  // Only use Unleash value if the flag is not overridden locally
  if (convertedFlagOverride === null && query[flagName] === undefined) {
    return unleashFlagEnabled;
  } else {
    return convertedFlagOverride || qsFlagOverrideEnabled;
  }
};

const convertStringToBoolean = (value): boolean | null => {
  if (value === "true") return true;
  if (value === "false") return false;

  return null;
};
